import {
  DoNotDisturbOn,
  ExpandMore,
  InfoOutlined,
  WarningAmber,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { useAppSelector } from "src/app/hooks"
import { RootState } from "src/app/store"
import { selectMe } from "src/features/me/meSlice"
import { selectSaThingById } from "src/features/things/thingsSlice"
import {
  useGetThingsQuery,
  useUpdateThingsMutation,
} from "../features/things/thingsApi"
import {
  buildOverridePatches,
  deriveMigrationState,
  flagsForState,
  hasIncoherentFlags,
  isHiddenFromUsers,
  LockerMigrationState,
  previewPair,
  readMigrationFlags,
  resolveCounterpart,
  summarizePatch,
} from "./lockerMigrationOverride"
import SaLockerMigrationAncestry from "./SaLockerMigrationAncestry"
import SaLockerMigrationConfirmDialog from "./SaLockerMigrationConfirmDialog"
import SaLockerMigrationHeader from "./SaLockerMigrationHeader"
import SaLockerMigrationPairDiagram from "./SaLockerMigrationPairDiagram"
import SaLockerMigrationStateOptions from "./SaLockerMigrationStateOptions"
import { isOutOfService } from "./lockerServiceState"

/**
 * Super-admin escape hatch for a locker whose SSTv2 migration flags no longer
 * describe reality (DEV-193). Rules live in `lockerMigrationOverride.ts`; this
 * file collects intent and shows the operator exactly what will change.
 *
 * Collapsed by default: on a healthy locker this is one more thing to scroll
 * past, and an always-open form invites idle clicking on flags that decide
 * whether real users can see a real locker. It opens itself only when the flags
 * are already incoherent — the one case where the page should nag.
 */
export const SaLockerMigrationOverride: FC<{ locker: Locker }> = ({
  locker,
}) => {
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const actor = me?.user?.email || me?.user?.id

  const flags = useMemo(() => readMigrationFlags(locker), [locker])
  const currentState = deriveMigrationState(flags)
  const counterpart = useMemo(() => resolveCounterpart(locker), [locker])
  const incoherent = hasIncoherentFlags(flags)
  const outOfService = isOutOfService(locker)

  const { refetch: refetchCounterpart } = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetLockers,
        "ap.ids[0]": counterpart?.id,
      },
    },
    { skip: !counterpart?.id },
  )
  const counterpartLocker = useAppSelector((s: RootState) =>
    selectSaThingById(s, counterpart?.id || ""),
  ) as Locker | undefined

  const [open, setOpen] = useState(incoherent)
  const [targetState, setTargetState] =
    useState<LockerMigrationState>(currentState)
  const [reason, setReason] = useState("")
  const [reasonTouched, setReasonTouched] = useState(false)
  const [applyToCounterpart, setApplyToCounterpart] = useState(true)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [successorDraft, setSuccessorDraft] = useState(
    flags.supersededByLockerThingId || "",
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState(false)

  // Keep the advanced field truthful: it is the single source for the successor
  // pointer, so it has to follow the state radio rather than drift behind it.
  useEffect(() => {
    setSuccessorDraft(
      flagsForState(targetState, flags).supersededByLockerThingId || "",
    )
  }, [targetState, flags])

  const [updateThings, { isLoading: isSaving }] = useUpdateThingsMutation()

  // Timestamped at call time, not memo time: the operator can sit on the
  // confirmation dialog, and migrationNote must say when the write happened.
  const makePatches = useCallback(
    (now: string) =>
      buildOverridePatches({
        locker,
        targetState,
        supersededByLockerThingIdOverride: successorDraft,
        reason: reason.trim(),
        counterpart,
        applyToCounterpart,
        counterpartLocker,
        actor,
        now,
      }),
    [
      locker,
      targetState,
      successorDraft,
      reason,
      counterpart,
      applyToCounterpart,
      counterpartLocker,
      actor,
    ],
  )

  /** Preview only — the real write re-stamps `now` inside `onApply`. */
  const previewPatches = useMemo(() => makePatches("preview"), [makePatches])

  const preview = useMemo(
    () =>
      previewPair({
        locker,
        targetState,
        counterpart,
        counterpartLocker,
        applyToCounterpart,
      }),
    [locker, targetState, counterpart, counterpartLocker, applyToCounterpart],
  )

  const isDirty =
    targetState !== currentState ||
    (successorDraft || null) !== flags.supersededByLockerThingId
  const reasonMissing = reason.trim().length === 0
  const canApply = isDirty && !reasonMissing && !isSaving

  const resetDraft = () => {
    setTargetState(currentState)
    setReason("")
    setReasonTouched(false)
    setApplied(false)
    setError(null)
  }

  const onApply = async () => {
    try {
      setError(null)
      await updateThings({
        body: { things: makePatches(new Date().toISOString()) },
        params: { tenantId: locker.tenantId },
      }).unwrap()
      setConfirmOpen(false)
      setApplied(true)
      setReason("")
      setReasonTouched(false)
      if (counterpart?.id) refetchCounterpart()
    } catch (e: any) {
      setError(e?.data?.error || e?.message || String(e))
    }
  }

  const roleLabel = (isV2: boolean) => (isV2 ? "SST V2" : "SST V1")
  const primaryTitle = `${t("lockerMigrationOverride.thisLocker")} · ${roleLabel(
    locker.sstApiVersion === "v2",
  )}`
  const counterpartTitle = counterpart
    ? `${t("lockerMigrationOverride.pairedLocker")} · ${roleLabel(counterpart.role === "v2")}`
    : ""

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: incoherent ? "warning.main" : "divider",
      }}
    >
      <SaLockerMigrationHeader
        currentState={currentState}
        hidden={isHiddenFromUsers(flags)}
        outOfService={outOfService}
        incoherent={incoherent}
        open={open}
        onToggle={() => {
          if (open) resetDraft()
          setOpen((v) => !v)
        }}
      />

      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 2.5 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {t("lockerMigrationOverride.subtitle")}
          </Typography>

          {/* Why this sits here and not in a doc: the two panels on this page both
              end in "users cannot see this locker", and the durability difference
              between them is invisible from the UI. An operator who only wants to
              pull a unit from customer view should be told, at the moment of
              reaching for these flags, that the switch above does that without
              being reverted by the next partner event. */}
          <Alert
            icon={<InfoOutlined fontSize="inherit" />}
            severity="info"
            variant="outlined"
            sx={{ mb: 2, fontSize: "0.8rem" }}
          >
            {t("lockerMigrationOverride.vsServiceSwitch")}
          </Alert>

          {outOfService && (
            <Alert
              icon={<DoNotDisturbOn fontSize="inherit" />}
              severity="warning"
              variant="outlined"
              sx={{ mb: 2, fontSize: "0.8rem" }}
            >
              {t("lockerMigrationOverride.outOfServiceActive")}
            </Alert>
          )}

          {incoherent && (
            <Alert
              icon={<WarningAmber fontSize="inherit" />}
              severity="warning"
              variant="outlined"
              sx={{ mb: 2, fontSize: "0.8rem" }}
            >
              {t("lockerMigrationOverride.incoherentFlags")}
            </Alert>
          )}

          {/* ── Live before → after for the whole pair ── */}
          <SaLockerMigrationPairDiagram
            preview={preview}
            primaryTitle={primaryTitle}
            counterpartTitle={counterpartTitle}
          />

          {/* ── Target state ── */}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 2.5, mb: 1, fontWeight: 600 }}
          >
            {t("lockerMigrationOverride.setThisLockerTo")}
          </Typography>
          <SaLockerMigrationStateOptions
            value={targetState}
            currentState={currentState}
            onChange={(next) => {
              setApplied(false)
              setTargetState(next)
            }}
          />

          {counterpart && (
            <FormControlLabel
              sx={{ display: "flex", mt: 1.5, alignItems: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  sx={{ py: 0.25 }}
                  checked={applyToCounterpart}
                  onChange={(e) => setApplyToCounterpart(e.target.checked)}
                />
              }
              label={
                <Box sx={{ pt: 0.25 }}>
                  <Typography variant="body2">
                    {t("lockerMigrationOverride.applyToCounterpartShort")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("lockerMigrationOverride.applyToCounterpartHint")}
                  </Typography>
                </Box>
              }
            />
          )}

          <TextField
            required
            fullWidth
            size="small"
            sx={{ mt: 2.5 }}
            label={t("lockerMigrationOverride.reason")}
            placeholder={t("lockerMigrationOverride.reasonPlaceholder")}
            error={reasonTouched && reasonMissing}
            helperText={
              reasonTouched && reasonMissing
                ? t("lockerMigrationOverride.reasonRequired")
                : t("lockerMigrationOverride.reasonHelp")
            }
            value={reason}
            onBlur={() => setReasonTouched(true)}
            onChange={(e) => {
              setApplied(false)
              setReason(e.target.value)
            }}
          />

          {/* ── Advanced ── */}
          <Button
            size="small"
            onClick={() => setAdvancedOpen((v) => !v)}
            endIcon={
              <ExpandMore
                sx={{
                  transform: advancedOpen ? "rotate(180deg)" : "none",
                  transition: "transform 150ms",
                }}
              />
            }
            sx={{ textTransform: "none", mt: 1, color: "text.secondary" }}
          >
            {t("lockerMigrationOverride.advanced")}
          </Button>
          <Collapse in={advancedOpen}>
            <Box sx={{ pt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                migrationDisabled: {String(flags.migrationDisabled)}
                {" · "}pendingActivation: {String(flags.pendingActivation)}
              </Typography>
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                label="supersededByLockerThingId"
                value={successorDraft}
                disabled={targetState === "active"}
                onChange={(e) => setSuccessorDraft(e.target.value)}
                helperText={
                  targetState === "active"
                    ? t("lockerMigrationOverride.successorClearedHelp")
                    : t("lockerMigrationOverride.successorHelp")
                }
              />
              <Box sx={{ mt: 1.5 }}>
                <SaLockerMigrationAncestry locker={locker} />
              </Box>
            </Box>
          </Collapse>

          {error && (
            <Alert severity="error" variant="outlined" sx={{ mt: 2, fontSize: "0.8rem" }}>
              {error}
            </Alert>
          )}
          {applied && !error && (
            <Alert severity="success" variant="outlined" sx={{ mt: 2, fontSize: "0.8rem" }}>
              {t("lockerMigrationOverride.applied")}
            </Alert>
          )}

          {/* ── Action ── */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              {!isDirty
                ? t("lockerMigrationOverride.disabledNoChange")
                : reasonMissing
                  ? t("lockerMigrationOverride.disabledNoReason")
                  : t("lockerMigrationOverride.pendingWrites", {
                      count: previewPatches.length,
                    })}
            </Typography>
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={!canApply}
              onClick={() => setConfirmOpen(true)}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              {t("lockerMigrationOverride.review")}
            </Button>
          </Stack>
        </Box>
      </Collapse>

      <SaLockerMigrationConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onApply}
        isSaving={isSaving}
        rows={previewPatches.map((p) => ({
          id: p.id,
          changes: summarizePatch(
            p,
            readMigrationFlags(p.id === locker.id ? locker : counterpartLocker),
          ),
        }))}
      />
    </Paper>
  )
}

export default SaLockerMigrationOverride
